import { Module } from 'vuex'

import { DashboardUseCase } from '@/application/dashboard/dashboardUseCase'
import { Course } from '@/domain/entities/problem/course'
import { CourseSolutionRecord } from '@/domain/entities/problem/courseSolutionRecord'
import { DashboardRepository } from '@/domain/repositories/dashboard/DashboardRepository'
import { ProgramLanguageType } from '@/domain/value_objects/language/programLanguageType'

interface CourseHeatMapState {
  courseStatisticsList: { [courseName: string]: { [programLanguage: string]: Course } }
  courseSolutionRecordList: CourseSolutionRecord[]
  courseSolutionRecordListDictionary: {
    [topic: string]: string[]
  }
  heatMapSeries: any[]
  heatMapXLabel: string[]
  chartOptions: any
  hasData: boolean
  isPlotting: boolean
  isLoading: boolean
  error: string | null
}

export const courseHeatMapModule = (repository: DashboardRepository): Module<CourseHeatMapState, any> => {
  const dashboardUseCase = new DashboardUseCase(repository)

  return {
    namespaced: true,
    state: {
      courseStatisticsList: {},
      courseSolutionRecordList: [],
      courseSolutionRecordListDictionary: {},
      heatMapSeries: [],
      heatMapXLabel: [],
      chartOptions: {},
      hasData: false,
      isPlotting: false,
      isLoading: false,
      error: null
    },
    mutations: {
      setCourseStatistics(
        state,
        { course, programLanguage }: { course: Course; programLanguage: ProgramLanguageType }
      ) {
        state.courseStatisticsList = {
          ...state.courseStatisticsList,
          [course.shortName]: {
            ...state.courseStatisticsList[course.shortName],
            [programLanguage]: course
          }
        }
      },
      clearCourseStatisticsList(state) {
        state.courseStatisticsList = {}
      },
      setCourseSolutoinRecordList(state, { records }: { records: CourseSolutionRecord[] }) {
        state.courseSolutionRecordList = records
      },
      setCourseSolutoinRecordListDictionary(state, { records }: { records: { [topic: string]: string[] } }) {
        for (const topic in records) {
          state.courseSolutionRecordListDictionary[topic] = []
          records[topic].forEach(element => {
            state.courseSolutionRecordListDictionary[topic].push(element)
          })
        }
      },
      setHeatMapSeries(state, { series }: { series: any[] }) {
        state.heatMapSeries = series
      },
      setHeatMapXLabel(state, { labels }: { labels: string[] }) {
        state.heatMapXLabel = labels
      },
      setChartOptions(state, { options }: { options: any }) {
        state.chartOptions = options
      },
      setHasData(state, hasData: boolean) {
        state.hasData = hasData
      },
      setIsPlotting(state, isPlotting: boolean) {
        state.isPlotting = isPlotting
      },
      setIsLoading(state, isLoading: boolean) {
        state.isLoading = isLoading
      },
      setError(state, error: string | null) {
        state.error = error
      }
    },
    actions: {
      async getCourseSolutoinRecordList({ state, commit }, { userId }: { userId: string }) {
        commit('setIsLoading', true)
        commit('setError', null)
        try {
          const res = await dashboardUseCase.getCourseSolutoinRecordList(userId)

          if (res == null || res.length === 0) return

          commit('setCourseSolutoinRecordList', { records: res })
        } catch (e) {
          commit('setError', e)
        } finally {
          commit('setIsLoading', false)
        }
      },
      async getCourseStatisticsListByLanguage(
        { state, commit },
        {
          courseName,
          userId,
          programLanguage
        }: { courseName: string; userId: string; programLanguage: ProgramLanguageType }
      ): Promise<Course | undefined> {
        commit('setIsLoading', true)
        commit('setError', null)
        try {
          const newLanguage = programLanguage.replace(' (Beta)', '').replace('#', '%23')
          if (
            state.courseStatisticsList[courseName] != undefined &&
            state.courseStatisticsList[courseName][newLanguage] != undefined
          )
            return state.courseStatisticsList[courseName][newLanguage]
          const res = await dashboardUseCase.getCourseStatisticsByLanguage(courseName, userId, programLanguage)
          commit('setCourseStatistics', { course: res, programLanguage: newLanguage })
          return res
        } catch (e) {
          commit('setError', e)
        } finally {
          commit('setIsLoading', false)
        }
      }
    },
    getters: {
      courseStatisticsList: state => state.courseStatisticsList,
      courseSolutionRecordList: state => state.courseSolutionRecordList,
      courseSolutionRecordListDictionary: state => state.courseSolutionRecordListDictionary,
      heatMapXLabel: state => state.heatMapXLabel,
      heatMapSeries: state => state.heatMapSeries,
      chartOptions: state => state.chartOptions,
      hasData: state => state.hasData,
      isPlotting: state => state.isPlotting,
      isLoading: state => state.isLoading,
      error: state => state.error
    }
  }
}
